import { motion } from "framer-motion";
import { Check, X, Minus, Crown, Clock, Gauge, Palette, Shield, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const criteria = [
  { label: "Prix moyen", us: "79 – 349 €", wix: "0 – 30 €/mois", freelance: "800 – 3 000 €", agency: "3 000 – 15 000 €" },
  { label: "Délai de livraison", us: "48h", wix: "Vous le faites", freelance: "2 – 4 semaines", agency: "1 – 3 mois" },
  { label: "Design premium unique", us: true, wix: false, freelance: "partial", agency: true },
  { label: "Animations fluides", us: true, wix: false, freelance: "partial", agency: "partial" },
  { label: "Dark & Light mode", us: true, wix: false, freelance: false, agency: "partial" },
  { label: "Visible sur Google (SEO)", us: true, wix: "partial", freelance: "partial", agency: true },
  { label: "Ultra rapide (score 95+)", us: true, wix: false, freelance: false, agency: "partial" },
  { label: "Parfait sur mobile", us: true, wix: "partial", freelance: true, agency: true },
  { label: "Bilingue FR/EN", us: true, wix: "partial", freelance: "partial", agency: true },
  { label: "Satisfait ou remboursé", us: true, wix: false, freelance: false, agency: false },
  { label: "Sans abonnement obligatoire", us: true, wix: false, freelance: true, agency: true },
  { label: "Aucune compétence requise", us: true, wix: false, freelance: true, agency: true },
];

function CellValue({ value }: { value: boolean | string }) {
  if (value === true) return <Check className="w-5 h-5 text-primary mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-muted-foreground/40 mx-auto" />;
  if (value === "partial") return <Minus className="w-5 h-5 text-enterprise-warning mx-auto" />;
  return <span className="text-sm font-medium">{value}</span>;
}

const SellComparison = () => {
  return (
    <section className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent" />
      <div className="container mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            <Crown className="w-4 h-4" />
            Comparatif honnête
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4">
            Pourquoi nous choisir plutôt{" "}
            <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
              qu'une autre solution ?
            </span>
          </h2>
          <p className="text-muted-foreground text-sm sm:text-lg">
            On ne vous vend pas du rêve. Voici un comparatif transparent avec les alternatives du marché.
          </p>
        </motion.div>

        {/* Desktop table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="max-w-5xl mx-auto hidden md:block"
        >
          <div className="rounded-2xl border border-border/50 overflow-hidden bg-card/50 backdrop-blur-sm">
            {/* Header */}
            <div className="grid grid-cols-5 bg-secondary/40 border-b border-border/30">
              <div className="p-4 text-sm font-semibold text-muted-foreground">Critère</div>
              <div className="p-4 text-center bg-primary/10 border-x border-primary/20">
                <div className="flex flex-col items-center gap-1">
                  <Crown className="w-5 h-5 text-primary" />
                  <span className="font-bold text-primary text-sm">Folio Studio</span>
                  <span className="text-xs text-primary/70">Notre service</span>
                </div>
              </div>
              <div className="p-4 text-center">
                <span className="font-semibold text-sm text-muted-foreground">Wix / Squarespace</span>
                <p className="text-xs text-muted-foreground/60">DIY</p>
              </div>
              <div className="p-4 text-center">
                <span className="font-semibold text-sm text-muted-foreground">Freelance</span>
                <p className="text-xs text-muted-foreground/60">Indépendant</p>
              </div>
              <div className="p-4 text-center">
                <span className="font-semibold text-sm text-muted-foreground">Agence web</span>
                <p className="text-xs text-muted-foreground/60">Studio</p>
              </div>
            </div>

            {/* Rows */}
            {criteria.map((row, i) => (
              <div
                key={row.label}
                className={`grid grid-cols-5 border-b border-border/20 last:border-0 ${i % 2 === 0 ? "" : "bg-secondary/10"}`}
              >
                <div className="p-4 text-sm font-medium flex items-center">{row.label}</div>
                <div className="p-4 flex items-center justify-center bg-primary/5 border-x border-primary/10">
                  <CellValue value={row.us} />
                </div>
                <div className="p-4 flex items-center justify-center">
                  <CellValue value={row.wix} />
                </div>
                <div className="p-4 flex items-center justify-center">
                  <CellValue value={row.freelance} />
                </div>
                <div className="p-4 flex items-center justify-center">
                  <CellValue value={row.agency} />
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-4 max-w-lg mx-auto">
          {criteria.map((row, i) => (
            <motion.div
              key={row.label}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="p-4 rounded-xl border border-border/50 bg-card/50"
            >
              <p className="font-semibold text-sm mb-3">{row.label}</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10 border border-primary/20">
                  <CellValue value={row.us} />
                  <span className="font-medium text-primary">Nous</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50">
                  <CellValue value={row.wix} />
                  <span className="text-muted-foreground">DIY</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50">
                  <CellValue value={row.freelance} />
                  <span className="text-muted-foreground">Freelance</span>
                </div>
                <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/50">
                  <CellValue value={row.agency} />
                  <span className="text-muted-foreground">Agence</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Bottom CTA */}
        <motion.div
          className="text-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <p className="text-muted-foreground text-sm mb-4">
            La qualité d'une agence, au prix d'un template, livré en 48h.
          </p>
          <a href="#pricing">
            <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90 shadow-lg shadow-primary/25 group">
              Voir les tarifs
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default SellComparison;
